import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  Divider,
  Fade,
  Avatar,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import StageService from "../services/stageService";

// Simple decryption for URL parameters
const decryptData = (encryptedData) => {
  try {
    let base64 = encryptedData
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    while (base64.length % 4) {
      base64 += "=";
    }

    const decoded = decodeURIComponent(
      escape(atob(base64))
    );

    return JSON.parse(decoded);

  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

const ChooseAssessmentDate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkDataFilled, setCheckDataFilled] = useState(false);
  const [applicationStage, setApplicationStage] = useState(null);

  // Get and decrypt parameters from URL
  const encryptedData = searchParams.get("data");
  const decryptedParams = encryptedData ? decryptData(encryptedData) : null;
  const appId = decryptedParams?.appId || null;
  const orgId = decryptedParams?.orgId || null;
  const labName = decryptedParams?.labName || null;
  const labType = decryptedParams?.labType || null;
  const labId = decryptedParams?.labId || null;
  const standard = decryptedParams?.standard || null;
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    password: "",
    loading: false,
  });

  // Check if decryption was successful
  if (encryptedData && !decryptedParams) {
    toast.error(
      "Invalid or corrupted data. Please try again from the dashboard."
    );
  }

  // State for assessment date ranges
  const [assessmentDates, setAssessmentDates] = useState({
    range1: { from: "", to: "" },
    range2: { from: "", to: "" },
    range3: { from: "", to: "" },
  });

  // State for document review date
  const [documentReviewDate, setDocumentReviewDate] = useState("");

  useEffect(() => {
    const getApplicationStage = async (appId) => {
      try {
        if (!appId) return;

        const response = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5000/api"
          }/laboratory/application-stage/${appId}`
        );
        const result = await response.json();

        if (result.success) {
          setApplicationStage(result.stage);
        } else {
          setApplicationStage("1"); // Default to 1
        }
      } catch (error) {
        console.error("Error fetching application stage:", error);
        setApplicationStage("1"); // Default to 1
      }
    };
    getApplicationStage(appId);
  }, [appId]);

  // Fetch existing assessment dates on component mount
  useEffect(() => {
    const fetchAssessmentDates = async () => {
      if (!appId || !orgId) return;

      try {
        const url = `${process.env.REACT_APP_API_URL}/assessor/GetAssessmentPlan?appid=${appId}&orgid=${orgId}&Assessmenttype=Main`;
        const resp = await axios.get(url);
        const data = resp.data;

        // Bind dates if they exist, regardless of isstatus value
        if (data && Array.isArray(data.Date) && data.Date.length > 0) {
          const newAssessmentDates = {};

          data.Date.forEach((d, idx) => {
            if (idx < 3) {
              // Only bind first 3 date ranges
              const rangeKey = `range${idx + 1}`;
              // Convert date format from DD/MM/YYYY to YYYY-MM-DD
              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                // Handle DD/MM/YYYY format
                const parts = dateStr.split("/");
                if (parts.length === 3) {
                  const [day, month, year] = parts;
                  return `${year}-${month.padStart(2, "0")}-${day.padStart(
                    2,
                    "0"
                  )}`;
                }
                return "";
              };

              newAssessmentDates[rangeKey] = {
                Id: d.Id || "",
                from: formatDate(d.Datefrom) || "",
                to: formatDate(d.Dateto) || "",
              };
            }
          });

          // Merge with existing state to preserve any ranges not in API response
          setAssessmentDates((prev) => ({
            range1: newAssessmentDates.range1 || prev.range1,
            range2: newAssessmentDates.range2 || prev.range2,
            range3: newAssessmentDates.range3 || prev.range3,
          }));

          // If dates exist, mark as filled
          setCheckDataFilled(true);
        }
      } catch (error) {
        console.error("Error fetching assessment dates:", error);
        // Don't show error toast on fetch failure - user can still enter dates
      }
    };

    fetchAssessmentDates();
  }, [appId, orgId]);

  // Fetch document review date
  useEffect(() => {
    const fetchDocumentReviewDate = async () => {
      if (!appId || !orgId) return;

      try {
        const url = `${process.env.REACT_APP_API_URL}/assessor/GetDocumentReviewDate?appid=${appId}&orgid=${orgId}`;
        const resp = await axios.get(url);
        const data = resp.data;

        if (data && data.isstatus && data.date) {
          // Format date if needed (assuming it comes in a format we can use)
          setDocumentReviewDate(data.date || "");
        }
      } catch (error) {
        console.error("Error fetching document review date:", error);
      }
    };

    fetchDocumentReviewDate();
  }, [appId, orgId]);

  // Helper function to get next day after a given date
  const getNextDay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0];
  };

  // Handle input changes for date ranges
  const handleDateChange = (rangeIndex, dateType, value) => {
    const currentRange = assessmentDates[`range${rangeIndex}`];

    // Validate: From Date should not be greater than To Date
    if (dateType === "from" && currentRange.to && value > currentRange.to) {
      toast.error(
        `Period ${rangeIndex}: From Date cannot be greater than To Date`
      );
      return;
    }

    // Validate: To Date should not be less than From Date
    if (dateType === "to" && currentRange.from && value < currentRange.from) {
      toast.error(
        `Period ${rangeIndex}: To Date cannot be less than From Date`
      );
      return;
    }

    setAssessmentDates((prev) => ({
      ...prev,
      [`range${rangeIndex}`]: {
        ...prev[`range${rangeIndex}`],
        [dateType]: value,
      },
    }));
  };

  // Handle form submission
  const handleSave = async () => {
    // Validation: Check if all three date ranges are provided
    const ranges = [
      assessmentDates.range1,
      assessmentDates.range2,
      assessmentDates.range3,
    ];

    for (let i = 0; i < ranges.length; i++) {
      if (!ranges[i].from || !ranges[i].to) {
        toast.error(
          `Please select both from and to dates for Assessment Period ${i + 1}`
        );
        return;
      }

      // Validate that 'from' date is before 'to' date
      if (new Date(ranges[i].from) >= new Date(ranges[i].to)) {
        toast.error(
          `From date must be before to date for Assessment Period ${i + 1}`
        );
        return;
      }
    }

    // Validation: Check if dates are in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < ranges.length; i++) {
      const fromDate = new Date(ranges[i].from);
      const toDate = new Date(ranges[i].to);

      if (fromDate <= today) {
        toast.error(
          `From date for Assessment Period ${i + 1} must be in the future`
        );
        return;
      }

      if (toDate <= today) {
        toast.error(
          `To date for Assessment Period ${i + 1} must be in the future`
        );
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare cd array expected by backend: [{ appid, orgid, Datefrom, Dateto }, ...]
      // Use unambiguous ISO date format (YYYY-MM-DD HH:mm:ss) to avoid SQL conversion errors
      const toSQLDateTime = (isoDate) => {
        if (!isoDate) return "";
        const [y, m, d] = isoDate.split("-");
        return `${y}-${m}-${d} 00:00:00`;
      };

      const cd = [
        assessmentDates.range1,
        assessmentDates.range2,
        assessmentDates.range3,
      ].map((r) => ({
        Id: r.Id || "",
        appid: appId,
        orgid: orgId,
        Datefrom: toSQLDateTime(r.from),
        Dateto: toSQLDateTime(r.to),
      }));

      // Call backend API
      const url = `${process.env.REACT_APP_API_URL}/assessor/Setdatedbycab`;
      const resp = await axios.post(url, cd);
      if (resp?.data) {
        if (resp.data.isstatus) {
          setCheckDataFilled(true);
          await StageService.updateApplicationStage(orgId, appId, "19a");
          toast.success(resp.data.Message || "Assessment date ranges saved successfully!");
          setTimeout(() => {
          navigate("/main/dashboard");
          }, 2000);
        } else {
          toast.error(resp.data.Message || "Failed to save assessment dates");
        }
      } else {
        toast.error("No response from server");
      }
    } catch (error) {
      console.error("Error saving assessment dates:", error);
      toast.error("Failed to save assessment dates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (checkDataFilled) {
      await StageService.updateApplicationStage(orgId, appId, "19a");
      toast.success("Submitted successfully!");
      navigate("/main/dashboard");
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/main/dashboard");
  };

  // Get minimum date (maximum of: documentReviewDate + 15 days OR 22 December 2025)
  const getMinDate = () => {
    const fixedDate = new Date("2025-12-22"); // 22 December 2025

    if (documentReviewDate) {
      const reviewDate = new Date(documentReviewDate);
      // Add 15 days to the review date
      reviewDate.setDate(reviewDate.getDate() + 15);

      // Return the later date between reviewDate+15days and 22 Dec 2025
      const maxDate = reviewDate > fixedDate ? reviewDate : fixedDate;
      return maxDate.toISOString().split("T")[0];
    }

    // If no document review date, return 22 Dec 2025
    return fixedDate.toISOString().split("T")[0];
  };

  // Function to handle password confirmation
  const confirmSaveAssessmentDates = async () => {
    if (!passwordDialog.password) return;

    // Validation: Check if all three date ranges are provided
    const ranges = [
      assessmentDates.range1,
      assessmentDates.range2,
      assessmentDates.range3,
    ];

    for (let i = 0; i < ranges.length; i++) {
      if (!ranges[i].from || !ranges[i].to) {
        toast.error(
          `Please select both from and to dates for Assessment Period ${i + 1}`
        );
        return;
      }

      // Validate that 'from' date is before 'to' date
      if (new Date(ranges[i].from) >= new Date(ranges[i].to)) {
        toast.error(
          `From date must be before to date for Assessment Period ${i + 1}`
        );
        return;
      }
    }

    // Validation: Check if dates are in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < ranges.length; i++) {
      const fromDate = new Date(ranges[i].from);
      const toDate = new Date(ranges[i].to);

      if (fromDate <= today) {
        toast.error(
          `From date for Assessment Period ${i + 1} must be in the future`
        );
        return;
      }

      if (toDate <= today) {
        toast.error(
          `To date for Assessment Period ${i + 1} must be in the future`
        );
        return;
      }
    }

    setPasswordDialog((prev) => ({ ...prev, loading: true }));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: orgId,
          password: passwordDialog.password,
        }
      );
      if (response.data.success) {
        // toast.success("Password confirmed. Proceeding to save assessment dates.");
        setPasswordDialog({ open: false, password: "", loading: false });
        await handleSave();
      } else {
        toast.error("Invalid password. Please try again.");
        setPasswordDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Error confirming password:", error);
      toast.error("Invalid password. Please try again.");
      setPasswordDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header with Gradient Background */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
          mt: 8,
          mr: 3,
          ml: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": {
                color: "rgba(255,255,255,0.7)",
              },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBack}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Dashboard
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Choose Assessment Date
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <CalendarIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {labName || "-"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    {labType || "-"}
                  </Typography>
                  <Chip
                    label={standard || "-"}
                    variant="outlined"
                    size="small"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: { xs: 2, md: 3 }, maxWidth: "1400px", mx: "auto" }}>
        {/* Assessment Dates Form */}
        <Fade in={true} timeout={1200}>
          <Card
            sx={{
              mb: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: 2.5,
                color: "white",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, letterSpacing: "-0.5px" }}
              >
                📅 Select Assessment Dates
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: "rgba(102,126,234,0.08)",
                  border: "1px solid rgba(102,126,234,0.2)",
                  "& .MuiAlert-icon": {
                    color: "primary.main",
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Please provide three different date ranges • All dates must be
                  in the future
                </Typography>
              </Alert>

              <Grid container spacing={4}>
                {/* Assessment Period 1 */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: "#333", fontWeight: 600 }}
                  >
                    Assessment Period 1
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        From Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range1.from}
                        onChange={(e) =>
                          handleDateChange(1, "from", e.target.value)
                        }
                        inputProps={{
                          min: getMinDate(),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        To Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range1.to}
                        onChange={(e) =>
                          handleDateChange(1, "to", e.target.value)
                        }
                        inputProps={{
                          min: assessmentDates.range1.from || getMinDate(),
                        }}
                        disabled={!assessmentDates.range1.from}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Assessment Period 2 */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: "#333", fontWeight: 600 }}
                  >
                    Assessment Period 2
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        From Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range2.from}
                        onChange={(e) =>
                          handleDateChange(2, "from", e.target.value)
                        }
                        disabled={!assessmentDates.range1.to}
                        inputProps={{
                          min: getNextDay(assessmentDates.range1.to),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        To Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range2.to}
                        onChange={(e) =>
                          handleDateChange(2, "to", e.target.value)
                        }
                        disabled={!assessmentDates.range2.from}
                        inputProps={{
                          min: assessmentDates.range2.from,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Assessment Period 3 */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, color: "#333", fontWeight: 600 }}
                  >
                    Assessment Period 3
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        From Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range3.from}
                        onChange={(e) =>
                          handleDateChange(3, "from", e.target.value)
                        }
                        disabled={!assessmentDates.range2.to}
                        inputProps={{
                          min: getNextDay(assessmentDates.range2.to),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        To Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        value={assessmentDates.range3.to}
                        onChange={(e) =>
                          handleDateChange(3, "to", e.target.value)
                        }
                        disabled={!assessmentDates.range3.from}
                        inputProps={{
                          min: assessmentDates.range3.from,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "#1976d2",
                            },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              {/* Submit Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: 3,
                }}
              >
              { applicationStage === "19" && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SendIcon />}
                  onClick={setPasswordDialog.bind(this, { open: true, password: "", loading: false })}
                  disabled={loading}
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    px: 5,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "1rem",
                    boxShadow: "0 4px 12px rgba(102,126,234,0.4)",
                    textTransform: "none",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5568d3 0%, #6b3f92 100%)",
                      boxShadow: "0 6px 16px rgba(102,126,234,0.6)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(0,0,0,0.12)",
                      color: "rgba(0,0,0,0.26)",
                    },
                  }}
                >
                  {loading ? "Submitting..." : "Save Assessment Dates"}
                </Button>
              )}
                 {/* {checkDataFilled && applicationStage === "19" && (
                //   <Button
                //     variant="contained"
                //     size="large"
                //     startIcon={<SaveIcon />}
                //     onClick={handleSubmit}
                //     disabled={loading}
                //     sx={{
                //       background:
                //         "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                //       color: "white",
                //       px: 5,
                //       py: 1.5,
                //       borderRadius: 2,
                //       fontWeight: 600,
                //       fontSize: "1rem",
                //       boxShadow: "0 4px 12px rgba(17,153,142,0.4)",
                //       textTransform: "none",
                //       "&:hover": {
                //         background:
                //           "linear-gradient(135deg, #0e8073 0%, #2ed66c 100%)",
                //         boxShadow: "0 6px 16px rgba(17,153,142,0.6)",
                //       },
                //       "&.Mui-disabled": {
                //         background: "rgba(0,0,0,0.12)",
                //         color: "rgba(0,0,0,0.26)",
                //       },
                //     }}
                //   >
                //     {loading ? "Submitting..." : "Submit"}
                //   </Button>
                 )} */}
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Box>
      <Dialog
        open={passwordDialog.open}
        onClose={() =>
          !passwordDialog.loading &&
          setPasswordDialog({ open: false, password: "", loading: false })
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: "white",
            p: 2.5,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ✅ Confirm Save Assessment Dates
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: "text.secondary" }}
          >
            Please enter your password to confirm saving the assessment dates.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Enter Your Password"
            placeholder="Enter password"
            value={passwordDialog.password}
            onChange={(e) =>
              setPasswordDialog((prev) => ({ ...prev, password: e.target.value }))
            }
            disabled={passwordDialog.loading}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter" && !passwordDialog.loading) {
                confirmSaveAssessmentDates();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() =>
              setPasswordDialog({ open: false, password: "", loading: false })
            }
            disabled={passwordDialog.loading}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSaveAssessmentDates}
            variant="contained"
            disabled={passwordDialog.loading || !passwordDialog.password}
            sx={{
              background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              borderRadius: 2,
              "&:hover": {
                background: "linear-gradient(135deg, #0e8073 0%, #2ed66c 100%)",
              },
              "&:disabled": {
                background: "rgba(0,0,0,0.12)",
                color: "rgba(0,0,0,0.26)",
              },
            }}
          >
            {passwordDialog.loading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Confirm & Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChooseAssessmentDate;
